import { findDOMNode } from 'react-dom';
import { isValidType } from '@platform/template';
import { getViewportBoundary, switchTypes, presetColumns } from '../../utils'

export default function useDOM(Table) {
    return Table;
}


useDOM.displayName = 'useDOM';
useDOM.dependence = [];
useDOM.hooks = function(){
    this.store.focusedCell = {};
    return {
        setLastFocusedRow: rowKey => {
            this.store.lastFocusedRow = rowKey;
        },
        getLastFocusedRow: () => {
            return this.store.lastFocusedRow;
        },
        // 只处理合法和可用的单元格
        moveTo: (rowKey, colKey, step = {}) => {
            let { right = 0, bottom = 0, loop = true, _originRowKey = rowKey, _originColKey = colKey } = step;
            if (rowKey && colKey) {
                // 处理right
                let status = this.getStatus(),
                    leafColumnKeys = this.store.visibleLeafColumnKeys.filter(
                        key => 
                            !presetColumns.has(key) && // 合法类型
                            // isValidType(this.getColumnProps(key, 'itemtype')) && // 合法类型
                            // (status === 'browse' || !this.getColumnProps(key, 'disabled')) && // 浏览态或者非禁用
                            !this.getColumnProps(key, 'tabskip') // 模板属性
                    ),
                    visibleRows = this.getVisibleRows(),
                    leafIndex = leafColumnKeys.findIndex((columnKeys) => colKey === columnKeys),
                    columnsCount = leafColumnKeys.length,
                    bottomOffset = 0,
                    firstRow = visibleRows[0]?.key,
                    lastRow = visibleRows[visibleRows.length - 1]?.key,
                    firstCol = leafColumnKeys[0],
                    lastCol = leafColumnKeys[columnsCount - 1];

                // 到边界时退出
                if (!loop) {
                    if (rowKey === firstRow && colKey === firstCol && (right < 0 || bottom < 0)) {
                        return null;
                    }
    
                    if (rowKey === lastRow && colKey === lastCol && (right > 0 || bottom > 0)) {
                        return null;
                    }
                }

                leafIndex += right;

                if (leafIndex > columnsCount - 1) {
                    bottomOffset = Math.floor(leafIndex/columnsCount);
                    leafIndex = leafIndex % columnsCount;
                } else if (leafIndex < 0) {
                    bottomOffset = Math.floor(leafIndex/columnsCount);
                    leafIndex = leafIndex % columnsCount + columnsCount;
                }
                
                let finalCode = leafColumnKeys[leafIndex];

                // 处理bottom
                let { viewIndex } = this.getRowData(rowKey),
                    { visibleRowKeys } = this.store;

                viewIndex += (bottom + bottomOffset);
                viewIndex = (viewIndex + visibleRowKeys.length) % visibleRowKeys.length; // 防止越界
                let finalRowKey = visibleRowKeys[viewIndex];
                
                if (
                    status !== 'browse' && 
                    !this.getCellProps(_originRowKey, _originColKey, 'disabled') && // 只有原始单元格是非禁用状态，才跳过禁用的单元格 —— NCC-234282
                    this.getCellProps(finalRowKey, finalCode, 'disabled')
                ) {
                    return this.moveTo(finalRowKey, finalCode, { ...step, _originRowKey, _originColKey });
                }

                return {
                    rowKey: finalRowKey,
                    colKey: finalCode,
                };
            }
            return null;
        },
        scrollIntoView: ({ rowKey, colKey }) => {
            let TableInstance = this.getComponent('Table')?.current;
            if(!TableInstance) {return;};

            let TableDOM = findDOMNode(TableInstance);
            if (!TableDOM || this.getConfig()?.gridType) {
                return;
            }

            let width = TableDOM.clientWidth,
                height = TableDOM.clientHeight,
                boundary = getViewportBoundary(TableDOM);

            let cellInstance = this.getComponent(this.itemIDManager.getID({ rowKey, colKey }))?.current;

            if (cellInstance) {
                let cellDOM = findDOMNode(cellInstance),
                    rect = cellDOM.getBoundingClientRect();
                    // 已经在视口范围了，不用再滚动了
                    // 因为有固定列，所以不能用scrollIntoView
                    if (
                        rect.top > boundary.top && 
                        rect.bottom < boundary.bottom &&
                        rect.left > boundary.left &&
                        rect.right < boundary.right
                    ) {
                        return;
                    }
            }

            if (rowKey && this.store.rowDataMap[rowKey]) {
                let verticalScrollElement = TableDOM.querySelector('.vertical-scroll-bar'),
                    viewHeight = this.store.rowHeightOffsets?.[rowKey] ?? this.store.rowDataMap[rowKey].dataIndex * TableInstance.props.rowHeight;
                
                verticalScrollElement.scrollTop = viewHeight - height / 2;
            }

            if (colKey) {
                let horizontalScrollElement = TableDOM.querySelector('.horizontal-scroll-bar'),
                    viewWidth = this.store.columnWidthOffsets?.[colKey];
                
                horizontalScrollElement.scrollLeft = viewWidth - width / 2;
            }
        },
        focusOnRow: (rowKey) => {
            let TableInstance = this.getComponent('Table')?.current;
            let focusedCell = this.getFocusedCell();
            if (focusedCell.rowKey === rowKey) {return;};

            findDOMNode(TableInstance).querySelector(`tr[rowkey='${rowKey}']`)?.focus();
        },
        focusOnCell: (rowKey, colKey, focus = true) => {
            let focusedCell = this.store.focusedCell,
                prevFocusedCell = focusedCell;

            if (focusedCell.rowKey === rowKey && focusedCell.colKey === colKey) {
                return;
            }
            
            this.store.focusedCell = { rowKey, colKey }
            // 由于虚拟滚动，滚动后dom可能会消失，所以先blur，触发blur（编辑后）事件
            document.activeElement.blur();
            this.scrollIntoView({ rowKey, colKey });
            
            let TableInstance = this.getComponent('Table')?.current;

            // 要先滚动到视口，再聚焦
            setTimeout(() => {
                if(this.store.focusedCell?.rowKey !== rowKey || this.store.focusedCell?.colKey !== colKey) {
                    //改变了 就不要聚焦了
                    return;
                }; 
                let cell = findDOMNode(TableInstance)?.querySelector(`td[rowkey='${rowKey}'][colkey='${colKey}'] .table-cell`);
                if (cell) {
                    if (focus && !cell.contains(document.activeElement)) {
                        cell?.focus();
                    }
                    let status = this.getStatus(),
                        { itemtype } = this.getColumnProps(colKey);
                    if (status === 'browse' && !switchTypes.has(itemtype)) {
                        let itemId = this.itemIDManager.getID({ colKey, rowKey }),
                        prevId = this.itemIDManager.getID(prevFocusedCell);
                    
                        prevId && this.updateComponent(prevId);
                        itemId && this.updateComponent(itemId);
                    }
                }
            }, 80);
        },
        blurOnCell: (rowKey, colKey) => {
            let { focusedCell } = this.store;
            if (focusedCell.rowKey === rowKey && focusedCell.colKey === colKey) {
                this.store.lastFocusedCell = { rowKey, colKey };
                this.store.focusedCell = {};
            }
        },
        setFocusedCell: (rowKey, colKey) => {
            this.store.lastFocusedCell = this.store.focusOnCell;
            this.store.focusedCell = {
                rowKey, 
                colKey,
            };
        },
        getFocusedCell: () => {
            return this.store.focusedCell || {};
        },
        getLastFocusedCell: () => {
            return this.store.lastFocusedCell || {};
        },
        setLastFocusedCell: (rowKey, colKey) => {
            this.store.lastFocusedCell = { rowKey, colKey };
        }
    };
}

useDOM.propsGenerator = function(props) {
    let { store, cellProps } = props;

    return {
        ...props,
        cellProps: (rowInfo, colInfo) => {
            let rawCellProps = cellProps?.(rowInfo, colInfo) ?? {};
            return {
                ...rawCellProps,
                onFocus: (e) => {
                    // 鼠标点的时候不走下面
                    if (!e.currentTarget.contains(e.target)) {
                        return;
                    }

                    // YBP-SCM-BUG-12129
                    // if (!colInfo.col.fixed) {
                    store.setFocusedCell(rowInfo.row.key, colInfo.col.key);
                    // }
                },
                onBlur: () => {
                    store.blurOnCell(rowInfo.row.key, colInfo.col.key);
                },
            }
        }
    };
};
